import { useMemo, useEffect, useState } from 'react'

import { TAlert, TCheckinWidgetBox, TCheckinWidgetTypes, TTagging } from 'interface'
import usePostCheckin from 'hooks/services/usePostCheckin'
import { themes } from 'common/constants'
import { isCheckinWidgetEnabled } from 'utils'
import { tagEvent } from 'utils/tagging'
import OrangeFridayBand from 'assets/icons/orange-friday-band.svg'

import { interWbSession } from '@interco/inter-webview-bridge'
import { Text } from '@interco/inter-ui/components/Text'
import { Loading } from '@interco/inter-ui/components/Loading'
import IcCalendar from '@interco/icons/core/action-navigation/ic_calendar'
import CheckCircle from '@interco/icons/core/status/ic_check_circle_fill'
import IcInterLoop from '@interco/icons/core/brands/ic_inter_loop'

import * as S from './styles'

const DayWidget = ({
  title,
  content: { state, text },
  alert,
  id,
}: TCheckinWidgetBox & { alert: TAlert; id: number }) => {
  const { mutate, isLoading, isSuccess } = usePostCheckin({
    alert,
    widget: state,
  })
  const Theme = useMemo(() => themes[state], [state])

  const RenderIcon = useMemo(() => {
    if (isLoading) return <Loading width={20} height={20} />

    const enabled = <S.Circle data-testid={`widget-middle-circle-${id}`} />
    const checkCircle = <CheckCircle width={24} height={24} color="var(--success500)" />

    const mapStateIcon: Record<TCheckinWidgetTypes, JSX.Element | null> = {
      DISABLED: <IcCalendar color="var(--gray200)" width={24} height={24} />,
      ENABLED: enabled,
      CHECKED: checkCircle,
      DAY_7_CHECKED: checkCircle,
      DAY_7_DISABLED: <IcInterLoop width={24} height={24} color="var(--primary500)" />,
      DAY_7_ENABLED: enabled,
    }

    return mapStateIcon[state]
  }, [isLoading, state, id])

  const checkIn = () => {
    if (isCheckinWidgetEnabled(state) && !isLoading && !isSuccess) {
      tagEvent({
        type: 'CHECKIN_FLUXO',
        content_action: 'toque',
        ref_type: 'botao',
        action_id: title,
      } as TTagging)
      mutate({})
    }
  }

  const textColorWeight = useMemo(() => {
    if (state.includes('DAY_7')) return 500
    return state === 'DISABLED' ? 300 : 400
  }, [state])

  const [app, setApplication] = useState<'PF' | 'PJ'>()

  useEffect(() => {
    const fetchAppInfo = async () => {
      const appInfo = await interWbSession.getAppInfo()
      setApplication(appInfo.application)
    }
    fetchAppInfo()
  }, [])

  return (
    <S.DayWidget
      onClick={checkIn}
      data-testid={`widget-${id}`}
      state={state}
      $border={Theme.border}
      $borderColor={Theme.borderColor}
      $bgColor={Theme.bgColor}
    >
      {state.includes('DAY_7') && state !== 'DAY_7_CHECKED' && app === 'PF' && (
        <S.OrangeFridayBand src={OrangeFridayBand} alt="Faixa Orange Friday" />
      )}
      <S.DayInfoContainer>
        <Text
          variant="headline-h3"
          colorWeight={state === 'DISABLED' ? 300 : 500}
          semiBold
          data-testid={`widget-text-header-${id}`}
        >
          {title}
        </Text>
        <Text variant="caption-1" colorWeight={textColorWeight} data-testid={`widget-text-${id}`}>
          {text}
        </Text>
      </S.DayInfoContainer>

      <S.IconContainer>{RenderIcon}</S.IconContainer>
    </S.DayWidget>
  )
}

export default DayWidget
